import packageAndVerification from "../img/package-and-verification.png";
import newsletters from "../img/newsletters.png";
import { FC, ReactNode } from "react";
import fullInboxBro from "../img/full-inbox-bro.png";
import domainSenderControls from "~/lib/components/img/domain-sender-controls.png";
import summaryEmail from "~/lib/components/img/summary-email-in-gmail-cropped.png";

export type HeroSide = "left" | "right" | number;

export const ImgWithCaption: FC<{ src: string; alt: string; classAdd?: string }> = ({
  src,
  alt,
  children,
  classAdd,
}) => (
  <>
    <img
      src={src}
      className={"border-gray-100 drop-shadow-xl mx-auto sm:w-96" + " " + classAdd ?? ""}
      alt={alt}
    />
    <div className="text-center text-sm ">{children}</div>
  </>
);

export const ImgSideHero: FC<{
  side: HeroSide;
  imgSrc: string;
  imgAlt: string;
  caption?: string;
  captionHref?: string;
  imgClassAdd?: string;
}> = ({ side, imgSrc, imgAlt, children, caption, captionHref, imgClassAdd }) => {
  if (typeof side == "number") {
    side = side % 2 == 0 ? "left" : "right";
  }
  const orderOverride = side == "left" ? "order-2" : "";
  return (
    <div className={`sm:grid sm:grid-cols-2 pb-8 sm:pb-32 sm:gap-x-8 `}>
      <div className={`${orderOverride}`}>
        <ImgWithCaption src={imgSrc} alt={imgAlt} classAdd={imgClassAdd}>
          {caption &&
            (captionHref ? (
              <a className="text-slate-400" href={captionHref}>
                {caption}
              </a>
            ) : (
              <>{caption}</>
            ))}
        </ImgWithCaption>
      </div>
      <div>
        <div className="mx-auto">{children}</div>
      </div>
    </div>
  );
};

export const HeroHeader: FC = ({ children }) => (
  <h1 className="text-2xl mt-4 lg:text-5xl font-bold">{children}</h1>
);

export const TextOnlySection: FC = ({ children }) => (
  <div className="pb-16 mx-auto sm:w-3/4">
    <div className=" text-center ">{children}</div>
  </div>
);

/* 

 feature idea evolution: 
 [] show sender toggles UI 
 [] show picture of bundled emails
 [] show immediate forward: gmail view with header controls on email 

*/

export const Features = ({ style }: { style: "simple" | "advanced" }) => {
  let imgSideCount = 0;
  return (
    <>
      <div className="">
        <TextOnlySection>
          <HeroHeader>Reduce your Inbox Clutter</HeroHeader>
        </TextOnlySection>
        <ImgSideHero
          imgAlt="A set of controls to turn off or allow emails from various companies "
          imgSrc={domainSenderControls}
          side={++imgSideCount}
          imgClassAdd="sm:w-48"
        >
          <HeroHeader>One click to block all email from a company</HeroHeader>
          <p>
            ThxNoThx gives you a new email address - when you give a company your ThxNoThx address,
            we can easily block all emails from them.
          </p>
          <p>
            Stop worrying about whether you should give a company your email address in exchange for
            a coupon - ThxNoThx means you can turn off their emails whenever you want.
          </p>
        </ImgSideHero>
        {style == "advanced" && (
          <ImgSideHero imgSrc={packageAndVerification} imgAlt="" side={++imgSideCount}>
            <HeroHeader>The information you want is now easy to find</HeroHeader>
            <p>
              ThxNoThx finds your <b>package tracking codes</b> and makes them quickly available.
            </p>
            <p>
              Quit sitting in your inbox hitting refresh:{" "}
              <b>ThxNoThx shows you verification codes immediately</b>
            </p>
          </ImgSideHero>
        )}
        <ImgSideHero
          side={++imgSideCount}
          imgAlt="A person being overwhelmed by email"
          imgSrc={fullInboxBro}
          caption="Internet illustrations by Storyset"
          captionHref="https://storyset.com/internet"
        >
          <HeroHeader>Simple and Reliable Email Unsubscribes</HeroHeader>
          <p>
            For companies that have your regular email address, we provide an unsubscribe service.
            Simply forward a marketing email from your regular inbox to unsubscribe@thxnothx.com and
            we will handle the rest. Your first 10 unsubscribes are free - no account setup needed.
            (Yes, really!)
          </p>
          <p>
            <b>If a company keeps sending you emails, we will follow up with them</b> to get you
            removed from their marketing email lists. In the US marketing emails sent after a user
            unsubscribes can result in a $16,000 fine per email.
          </p>
        </ImgSideHero>
        <ImgSideHero
          imgSrc={summaryEmail}
          side={++imgSideCount}
          imgAlt="show bundled email with email summaries"
        >
          <HeroHeader>Bundle marketing emails so they don't swamp your inbox</HeroHeader>
          <p>
            Reduce clutter in your inbox by bundling all your less important emails into one daily
            summary email.
          </p>
          <p>
            Any emails sent to your ThxNoThx inbox can be bundled together into a summary email and
            forwarded to your regular inbox. If emails from a company become more important, you can
            easily set emails from them to be immediately forwarded to your regular email.
          </p>
        </ImgSideHero>

        {/*
        <SideHero
          imgSrc="TODO.png"
          imgAlt="Your ThxNoThx inbox showing a per-sender view of inbox"
          side={++imgSideCount}
        >
          <HeroHeader>Sorting by sender makes everything better</HeroHeader>
          <p>Mute or unsubscribe from senders with one click</p>
          <p>
            Grouping emails by the sender means spammy senders only get one line
            in your inbox, not 5!
          </p>
        </SideHero> */}
        {style == "advanced" && (
          <ImgSideHero
            imgSrc={newsletters}
            imgAlt="Image showing a list of email newsletters with snippets of their contents"
            side={++imgSideCount}
          >
            <HeroHeader>The best way to read your newsletters</HeroHeader>
            <p>
              Get a single email each day with the full text of all your email newsletters. Or visit
              your newsletters page on ThxNoThx to get a pleasant distraction free reading
              experience.
            </p>
          </ImgSideHero>
        )}

        <TextOnlySection>
          <HeroHeader>
            You are the customer,
            <br />
            not advertisers
          </HeroHeader>
          <p>
            Why isn't our service free? At ThxNoThx we want to reduce the amount of junk in your
            inbox: <b>we don't show you ads or sell your information to marketers</b>, instead we
            charge for our service, which helps us cover important costs like servers and email
            services.
          </p>
        </TextOnlySection>

        {/*<SideHero
          imgSrc="TODO.png"
          imgAlt="yeah, what to do for this?"
          side={++imgSideCount}
        >
          <HeroHeader>Bring your own Domain</HeroHeader>
          <p>
            Already using your own domain to receive email? We'll show you how
            to connect it to ThxNoThx.{" "}
          </p>
        </SideHero> */}
      </div>
    </>
  );
};
